/**
 * What follows the pointer while objects are dragged in the objects tree — a picture of the
 * whole set, as rows like the tree's own: up to `MAX_ROWS` titles and a "+N more" line for the
 * rest — and scrolling the tree while the pointer nears its edges.
 */

/** Rows drawn before the rest is summed up. */
const MAX_ROWS = 3

/** Where the picture's top left corner sits from the pointer, up and to the left. */
export const DRAG_IMAGE_OFFSET = 16

/** How close to the tree's top / bottom edge the pointer scrolls it, and how fast at most. */
const SCROLL_EDGE = 32
const MAX_SCROLL_SPEED = 12

export type DragImageColorsT = {
    /** a row's background, like the tree's rows */
    paper: string
    text: string
    secondaryText: string
    /** the outline, as the drop indicator draws it */
    accent: string
    fontFamily: string
}

/**
 * An element to append to the body and move with the pointer (`transform`). It lets the pointer
 * through, so whatever is under it can still be found.
 */
export const createTreeDragImage = (titles: string[], colors: DragImageColorsT): HTMLElement => {
    const image = document.createElement("div")
    Object.assign(image.style, {
        position: "fixed",
        top: "0",
        left: "0",
        zIndex: "9999",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        padding: "4px",
        maxWidth: "240px",
        fontFamily: colors.fontFamily,
        fontSize: "13px",
        pointerEvents: "none",
    } as Partial<CSSStyleDeclaration>)

    const row = (text: string, secondary = false) => {
        const element = document.createElement("div")
        element.textContent = text
        Object.assign(element.style, {
            padding: "6px 10px",
            borderRadius: "4px",
            background: colors.paper,
            color: secondary ? colors.secondaryText : colors.text,
            boxShadow: `inset 0 0 0 1px ${colors.accent}`,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
        } as Partial<CSSStyleDeclaration>)
        return element
    }

    titles.slice(0, MAX_ROWS).forEach((title) => image.appendChild(row(title)))
    if (titles.length > MAX_ROWS) image.appendChild(row(`+${titles.length - MAX_ROWS} more`, true))
    return image
}

/** The nearest ancestor that scrolls vertically — the tree's own scrolling box. */
export const scrollParentOf = (element: HTMLElement): HTMLElement | null => {
    for (let parent = element.parentElement; parent; parent = parent.parentElement) {
        const { overflowY } = getComputedStyle(parent)
        if (
            (overflowY === "auto" || overflowY === "scroll") &&
            parent.scrollHeight > parent.clientHeight
        )
            return parent
    }
    return null
}

/**
 * How many pixels a frame to scroll by: up (negative) or down the closer the pointer is to the
 * box's top or bottom edge, 0 away from them.
 */
export const autoScrollSpeed = (pointerY: number, box: { top: number; bottom: number }) => {
    // 0 at the zone's inner end, 1 at the edge and beyond it
    const depth = (distance: number) => Math.min(1, distance / SCROLL_EDGE)
    if (pointerY < box.top + SCROLL_EDGE) {
        return -Math.ceil(MAX_SCROLL_SPEED * depth(box.top + SCROLL_EDGE - pointerY))
    }
    if (pointerY > box.bottom - SCROLL_EDGE) {
        return Math.ceil(MAX_SCROLL_SPEED * depth(pointerY - box.bottom + SCROLL_EDGE))
    }
    return 0
}
